// 통합 시스템 아키텍처 — 공공데이터/공공서비스 → AI 플랫폼 레이어드 구성도(히어로).
// 자족형 SVG: 엘리베이티드 카드 + 통합 데이터 버스 + 레드는 게이트웨이/오케스트레이션에만.
// 각 레이어는 옅은 배경 밴드 + 우측 상단 가로 라벨. 카드 높이는 타이트하게.

export default function SystemArchitecture() {
  const link = (x1: number, y1: number, x2: number, y2: number) => {
    const o = (y1 - y2) * 0.45;
    return `M${x1},${y1} C${x1},${(y1 - o).toFixed(1)} ${x2},${(y2 + o).toFixed(1)} ${x2},${y2}`;
  };
  const hex = (cx: number, cy: number, r: number) =>
    [-90, -30, 30, 90, 150, 210]
      .map((a) => {
        const t = (a * Math.PI) / 180;
        return `${(cx + r * Math.cos(t)).toFixed(1)},${(cy + r * Math.sin(t)).toFixed(1)}`;
      })
      .join(' ');

  // 레이어 배경 밴드 (top, height, 우측 상단 가로 라벨)
  const bands: { top: number; h: number; label: string }[] = [
    { top: 14, h: 78, label: 'ORCHESTRATION' },
    { top: 106, h: 78, label: 'GATEWAY' },
    { top: 198, h: 78, label: 'REASONING' },
    { top: 290, h: 82, label: 'KNOWLEDGE' },
    { top: 420, h: 84, label: 'DOMAIN' },
    { top: 518, h: 78, label: 'SOURCES' },
  ];

  // 연결선 (아래 → 위, 끝점에 화살표)
  const edges: number[][] = [
    [76, 542, 185, 500],
    [206, 542, 185, 500],
    [336, 542, 185, 500],
    [466, 542, 185, 500],
    [466, 542, 515, 500],
    [596, 542, 515, 500],
    [185, 444, 185, 406],
    [515, 444, 515, 406],
    [130, 384, 130, 368],
    [350, 384, 350, 368],
    [570, 384, 570, 368],
    [350, 314, 350, 272],
    [570, 314, 570, 272],
    [130, 314, 130, 176],
    [350, 222, 350, 176],
    [570, 222, 570, 176],
    [350, 126, 350, 84],
  ];
  // 레드 흐름(AI 스파인)
  const flows: number[][] = [
    [130, 314, 130, 176],
    [350, 222, 350, 176],
    [570, 222, 570, 176],
    [350, 126, 350, 84],
  ];

  const sources = [
    { x: 24, t: 'Data Infuser' },
    { x: 154, t: 'OpenAPI' },
    { x: 284, t: 'Data Verifier' },
    { x: 414, t: 'API 품질' },
    { x: 544, t: 'Swagger Doc' },
  ];
  const caps = [
    { x: 55, t: 'MCP Server' },
    { x: 275, t: 'VectorDB' },
    { x: 495, t: 'GraphDB' },
  ];
  const derived = [
    { x: 275, t: 'RAG', s: 'on VectorDB' },
    { x: 495, t: 'GraphRAG', s: 'on GraphDB' },
  ];
  const domains = [
    { x: 40, w: 290, t: '공공데이터' },
    { x: 370, w: 290, t: '공공서비스' },
  ];

  return (
    <div className="sa-root mh-arch">
      <style>{`
        .sa-root{width:100%;font-family:'pretendard',sans-serif;-webkit-font-smoothing:antialiased;}
        .sa-svg{width:100%;height:auto;display:block;}
        .sa-tag{fill:#9aa3b5;font-size:9.5px;font-weight:700;letter-spacing:1.8px;}
        .sa-src{fill:#565b70;font-size:11.5px;font-weight:600;}
        .sa-cap{fill:#181b24;font-size:13.5px;font-weight:700;letter-spacing:-.1px;}
        .sa-der{fill:#181b24;font-size:13.5px;font-weight:700;letter-spacing:-.1px;}
        .sa-dersub{fill:#8494ae;font-size:9px;font-weight:600;letter-spacing:.4px;}
        .sa-dom{fill:#181b24;font-size:16px;font-weight:800;letter-spacing:-.3px;}
        .sa-bar{fill:#ffffff;font-weight:800;letter-spacing:.2px;}
        .sa-barsub{fill:#ffffff;opacity:.82;font-size:10px;font-weight:600;letter-spacing:.4px;}
        .sa-bus{fill:#565b70;font-size:9px;font-weight:700;letter-spacing:3px;}
        .sa-edge{fill:none;stroke:#aeb6c8;stroke-width:1.4;}
        .sa-flow{fill:none;stroke:#e0455a;stroke-width:1.7;stroke-linecap:round;stroke-dasharray:3 12;opacity:.6;animation:sa-move 2.8s linear infinite;}
        @keyframes sa-move{to{stroke-dashoffset:-30;}}
        @media (prefers-reduced-motion:reduce){.sa-flow{animation:none;}}
      `}</style>

      <svg
        className="sa-svg"
        viewBox="0 0 700 610"
        style={{ height: 'auto', display: 'block' }}
        role="img"
        aria-label="프람트테크놀로지 데이터·AI 플랫폼 아키텍처"
      >
        <defs>
          <linearGradient id="sgCard" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" stopColor="#ffffff" />
            <stop offset="1" stopColor="#fbfcfe" />
          </linearGradient>
          <linearGradient id="sgDom" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" stopColor="#ffffff" />
            <stop offset="1" stopColor="#f1f3f9" />
          </linearGradient>
          <linearGradient id="sgBus" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" stopColor="#eef0f6" />
            <stop offset="1" stopColor="#dfe3ec" />
          </linearGradient>
          <linearGradient id="sgGate" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" stopColor="#e0455a" />
            <stop offset="1" stopColor="#c5384a" />
          </linearGradient>
          <linearGradient id="sgOrch" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" stopColor="#b8313d" />
            <stop offset="1" stopColor="#8f2532" />
          </linearGradient>
          <filter id="sgShCard" x="-20%" y="-30%" width="140%" height="170%">
            <feDropShadow dx="0" dy="2" stdDeviation="3" floodColor="#181b24" floodOpacity="0.10" />
          </filter>
          <filter id="sgShBar" x="-15%" y="-40%" width="130%" height="190%">
            <feDropShadow dx="0" dy="4" stdDeviation="7" floodColor="#b8313d" floodOpacity="0.26" />
          </filter>
          <marker id="sgArrow" markerWidth="8" markerHeight="8" refX="5" refY="3.5" orient="auto">
            <path d="M0.6,0.7 L6,3.5 L0.6,6.3 Z" fill="#8494ae" />
          </marker>
        </defs>

        {/* lane bands + 우측 상단 가로 라벨 */}
        {bands.map((b, i) => (
          <g key={`b${i}`}>
            <rect x={12} y={b.top} width={676} height={b.h} rx={16} fill="#f7f8fc" stroke="#edeff5" strokeWidth={1} />
            <text className="sa-tag" x={676} y={b.top + 15} textAnchor="end" dominantBaseline="central">
              {b.label}
            </text>
          </g>
        ))}

        {/* connectors (밴드 위, 카드 아래) */}
        {edges.map((e, i) => (
          <path key={`e${i}`} className="sa-edge" d={link(e[0], e[1], e[2], e[3])} markerEnd="url(#sgArrow)" />
        ))}
        {flows.map((e, i) => (
          <path key={`f${i}`} className="sa-flow" d={link(e[0], e[1], e[2], e[3])} />
        ))}

        {/* distribution bus */}
        <rect
          x={110}
          y={384}
          width={480}
          height={22}
          rx={11}
          fill="url(#sgBus)"
          stroke="#d4d9e4"
          strokeWidth={1}
          filter="url(#sgShCard)"
        />
        <text className="sa-bus" x={350} y={395} textAnchor="middle" dominantBaseline="central">
          통합 데이터 버스
        </text>

        {/* source cards (h44) */}
        {sources.map((s, i) => (
          <g key={`s${i}`}>
            <rect
              x={s.x}
              y={542}
              width={104}
              height={44}
              rx={11}
              fill="url(#sgCard)"
              stroke="#e8eaf1"
              strokeWidth={1}
              filter="url(#sgShCard)"
            />
            <rect x={s.x + 13} y={560.5} width={7} height={7} rx={2} fill="#8494ae" />
            <text className="sa-src" x={s.x + 28} y={564} dominantBaseline="central">
              {s.t}
            </text>
          </g>
        ))}

        {/* domain cards (h56) */}
        {domains.map((d, i) => (
          <g key={`d${i}`}>
            <rect
              x={d.x}
              y={444}
              width={d.w}
              height={56}
              rx={15}
              fill="url(#sgDom)"
              stroke="#d9deea"
              strokeWidth={1}
              filter="url(#sgShCard)"
            />
            <polygon points={hex(d.x + 24, 472, 8)} fill="#eef0f6" stroke="#8494ae" strokeWidth={1.5} />
            <text className="sa-dom" x={d.x + 42} y={472} dominantBaseline="central">
              {d.t}
            </text>
          </g>
        ))}

        {/* capability cards (h50) */}
        {caps.map((c, i) => (
          <g key={`c${i}`}>
            <rect
              x={c.x}
              y={314}
              width={150}
              height={50}
              rx={13}
              fill="url(#sgCard)"
              stroke="#dee2ec"
              strokeWidth={1}
              filter="url(#sgShCard)"
            />
            <rect x={c.x + 16} y={335} width={8} height={8} rx={2.5} fill="#565b70" />
            <text className="sa-cap" x={c.x + 33} y={340} dominantBaseline="central">
              {c.t}
            </text>
          </g>
        ))}

        {/* derived cards (h50, 타이틀+서브) */}
        {derived.map((d, i) => (
          <g key={`v${i}`}>
            <rect
              x={d.x}
              y={222}
              width={150}
              height={50}
              rx={13}
              fill="url(#sgCard)"
              stroke="#dee2ec"
              strokeWidth={1}
              filter="url(#sgShCard)"
            />
            <rect
              x={d.x + 17}
              y={243}
              width={8}
              height={8}
              rx={1.5}
              fill="#565b70"
              transform={`rotate(45 ${d.x + 21} 247)`}
            />
            <text className="sa-der" x={d.x + 34} y={241} dominantBaseline="central">
              {d.t}
            </text>
            <text className="sa-dersub" x={d.x + 34} y={258} dominantBaseline="central">
              {d.s}
            </text>
          </g>
        ))}

        {/* gateway bar (h50) */}
        <rect x={90} y={126} width={520} height={50} rx={15} fill="url(#sgGate)" filter="url(#sgShBar)" />
        <text
          className="sa-bar"
          x={350}
          y={147}
          textAnchor="middle"
          dominantBaseline="central"
          style={{ fontSize: 16 }}
        >
          AI Gateway
        </text>
        <text className="sa-barsub" x={350} y={165} textAnchor="middle" dominantBaseline="central">
          통합 게이트웨이 · MCP · RAG
        </text>

        {/* orchestration bar (h50) */}
        <rect x={90} y={34} width={520} height={50} rx={15} fill="url(#sgOrch)" filter="url(#sgShBar)" />
        <polygon points={hex(133, 59, 12)} fill="none" stroke="#ffffff" strokeWidth={1.5} opacity={0.55} />
        <polygon points={hex(133, 59, 5.5)} fill="#ffffff" opacity={0.7} />
        <text className="sa-bar" x={356} y={53} textAnchor="middle" dominantBaseline="central" style={{ fontSize: 17 }}>
          AI Orchestration
        </text>
        <text className="sa-barsub" x={356} y={71} textAnchor="middle" dominantBaseline="central">
          에이전트 오케스트레이션 레이어
        </text>
      </svg>
    </div>
  );
}
